import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HelpCircle, RotateCcw, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { casesApi, formsApi } from '../services/api';
import { mensagemDoErro } from '../utils/erroDaApi';

/** Períodos de jornada e remuneração são listas: digitar três faixas num campo de texto é ruim, e
 * elas já estão escritas nas observações. Para essas, o caminho é a proposta automática. */
const TIPOS_DE_LISTA = ['periodos_jornada', 'periodos_remuneracao'];

const CAMPO_POR_TIPO = {
  periodos_jornada: 'jornada_periodos',
  periodos_remuneracao: 'remuneracao_periodos',
};

/** Respostas a uma ordem da ficha. As duas últimas não mandam pedir nada: dizem que a peça já
 * cumpre a ordem (e o conferidor não viu) ou que ela não se aplica — e por isso exigem
 * justificativa, que fica gravada com o nome de quem respondeu. */
const OPCOES_DA_CONFIRMACAO = [['Sim, incluir', true], ['Não incluir', false]];
const OPCOES_DA_ORDEM = [...OPCOES_DA_CONFIRMACAO, ['Já atendida', 'atendida'], ['Não se aplica', 'nao_se_aplica']];
// Coerência da ficha (1.23): "sim" corrige o campo; "não se aplica" encerra, com justificativa.
const OPCOES_DA_COERENCIA = [['Sim, corrigir a ficha', true], ['Não se aplica', 'nao_se_aplica']];
function opcoesDaPergunta(origem) {
  if (origem === 'instrucao') return OPCOES_DA_ORDEM;
  if (origem === 'coerencia') return OPCOES_DA_COERENCIA;
  return OPCOES_DA_CONFIRMACAO;
}
const EXIGEM_JUSTIFICATIVA = ['atendida', 'nao_se_aplica'];
const ROTULO_DO_ESTADO = {
  dispensada: 'Não se aplica',
  nao_se_aplica: 'Não se aplica',
  atendida: 'Já atendida',
  nao_incluir: 'Não incluir',
};

function resumoDoPeriodo(periodo) {
  const intervalo = [periodo.inicio, periodo.fim].filter(Boolean).join(' a ');
  const detalhe = periodo.registrado
    ? `registrado ${periodo.registrado}${periodo.extrafolha ? ` + por fora ${periodo.extrafolha}` : ''}`
    : [periodo.shift_start, periodo.shift_end].filter(Boolean).join(' às ');
  return [intervalo, detalhe].filter(Boolean).join(' · ');
}

// A recusa estruturada (`{message, erros|recusadas}`) vira texto; detalhe em texto passa direto.
function erroDaApi(erro, padrao) {
  return mensagemDoErro(erro, padrao);
}

/**
 * A etapa de responder e resolver o que trava a geração.
 *
 * O bloqueio da v20 do caso Amauri chegou como "Pendências: Ordens do atendimento cumpridas" — o
 * usuário sabia que algo travou e não o que fazer. As informações existiam em três lugares
 * diferentes (campos que destravam o cálculo, dúvidas do atendimento, ordens da ficha que a peça
 * não cumpriu), nenhum respondível.
 *
 * Aqui elas viram perguntas com campo de resposta, e a resposta é gravada NA FICHA — no campo certo,
 * não num bloco de notas ao lado. Pergunta respondida some da lista.
 *
 * E há pendência que não tem resposta, porque não se aplica ao caso ("não houve pagamento por
 * fora"). Sem "Não se aplica" ela voltava a cada geração e contava contra a peça: agora é dispensada
 * com justificativa, e a dispensa pode ser reaberta.
 */
export default function PerguntasPanel({ caseId, onResolvido }) {
  const queryClient = useQueryClient();
  const [respostas, setRespostas] = useState({});
  const [dispensando, setDispensando] = useState(null);
  const [justificativaDispensa, setJustificativaDispensa] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['perguntas-caso', caseId],
    queryFn: () => casesApi.getPerguntas(caseId),
    enabled: !!caseId,
  });

  // Toda ação aqui muda a ficha e recalcula a nota da peça: cálculo e relatório saem do cache junto.
  const aplicarResultado = (resultado) => {
    queryClient.setQueryData(['perguntas-caso', caseId], {
      case_id: caseId, perguntas: resultado.perguntas, dispensadas: resultado.dispensadas || [],
    });
    ['calculo-pedidos', 'intake-form', 'petition', 'peticao-do-caso'].forEach(chave => (
      queryClient.invalidateQueries({ queryKey: [chave, caseId] })
    ));
    queryClient.invalidateQueries({ queryKey: ['cases-review'] });
  };

  const responder = useMutation({
    mutationFn: (payload) => casesApi.responderPerguntas(caseId, payload),
    onSuccess: (resultado) => {
      // Resposta fora do formato não foi gravada e a pergunta continua aberta: fica no campo para
      // ser corrigida, e o aviso diz qual e por quê.
      const recusadas = resultado.recusadas || [];
      setRespostas(atual => Object.fromEntries(
        Object.entries(atual).filter(([id]) => recusadas.some(r => r.id === id)),
      ));
      aplicarResultado(resultado);
      const restantes = resultado.perguntas.length;
      if (recusadas.length) {
        toast.error(`${recusadas.length} resposta(s) não gravada(s): ${recusadas[0].motivo}`, { duration: 10000 });
      }
      toast.success(
        restantes
          ? `${resultado.aplicadas.length} resposta(s) salva(s) na ficha. Faltam ${restantes}.`
          : 'Tudo respondido e salvo na ficha. Pode gerar a petição.',
      );
      if (!restantes) onResolvido?.();
    },
    onError: (erro) => toast.error(
      erroDaApi(erro, 'Não foi possível salvar as respostas.'), { duration: 10000 },
    ),
  });

  const dispensar = useMutation({
    mutationFn: ({ id, justificativa }) => casesApi.dispensarPendencia(caseId, id, justificativa),
    onSuccess: (resultado) => {
      setDispensando(null);
      setJustificativaDispensa('');
      aplicarResultado(resultado);
      toast.success('Pendência dispensada. A justificativa ficou registrada na ficha.');
      if (!resultado.perguntas.length) onResolvido?.();
    },
    onError: (erro) => toast.error(erroDaApi(erro, 'Não foi possível dispensar a pendência.')),
  });

  const reabrir = useMutation({
    mutationFn: (id) => casesApi.reabrirPendencia(caseId, id),
    onSuccess: (resultado) => {
      aplicarResultado(resultado);
      toast.success('Pendência reaberta.');
    },
    onError: (erro) => toast.error(erroDaApi(erro, 'Não foi possível reabrir a pendência.')),
  });

  const estruturar = useMutation({
    mutationFn: () => formsApi.estruturarObservacoes(caseId),
    onSuccess: ({ proposta, recusas }) => {
      const encontrados = Object.entries(CAMPO_POR_TIPO)
        .filter(([, campo]) => (proposta?.[campo] || []).length);
      if (!encontrados.length) {
        toast.error('Não localizei períodos nas observações que eu pudesse conferir na ficha.');
        return;
      }
      setRespostas(atual => {
        const novo = { ...atual };
        for (const [tipo, campo] of encontrados) {
          const pergunta = perguntas.find(p => p.tipo === tipo);
          if (pergunta) novo[pergunta.id] = { ...(novo[pergunta.id] || {}), valor: proposta[campo] };
        }
        return novo;
      });
      toast.success(
        `Períodos propostos a partir das observações. Confira antes de salvar.${
          recusas?.length ? ` ${recusas.length} descartado(s) por não constar da ficha.` : ''
        }`,
        { duration: 8000 },
      );
    },
    onError: (erro) => toast.error(erroDaApi(erro, 'Não foi possível ler as observações agora.')),
  });

  const perguntas = data?.perguntas || [];
  const dispensadas = data?.dispensadas || [];
  if (isLoading || (!perguntas.length && !dispensadas.length)) return null;
  const temListas = perguntas.some(p => TIPOS_DE_LISTA.includes(p.tipo));

  const definir = (id, campo, valor) => setRespostas(atual => ({
    ...atual, [id]: { ...(atual[id] || {}), [campo]: valor },
  }));

  // Só vai o que foi de fato respondido: confirmação precisa de sim/não (ou de "já atendida" / "não
  // se aplica" com justificativa), campo precisa de valor.
  const preenchidas = Object.entries(respostas).filter(([id, r]) => {
    const pergunta = perguntas.find(p => p.id === id);
    if (!pergunta) return false;
    if (EXIGEM_JUSTIFICATIVA.includes(r.valor)) return !!String(r.observacao ?? '').trim();
    if (pergunta.tipo === 'confirmacao') return typeof r.valor === 'boolean';
    if (Array.isArray(r.valor)) return r.valor.length > 0;
    return !!String(r.valor ?? '').trim();
  }).map(([id, r]) => (
    EXIGEM_JUSTIFICATIVA.includes(r.valor) ? [id, { ...r, justificativa: r.observacao }] : [id, r]
  ));

  return (
    <div className="card perguntas-panel">
      <div className="card-header">
        <HelpCircle size={15} className="perguntas-icone" />
        <span className="card-title">Responder para destravar</span>
        <span className="badge">{perguntas.length}</span>
      </div>
      <p className="perguntas-nota">
        A resposta é gravada na ficha e vale para todas as próximas gerações.
      </p>

      {temListas && (
        <button
          type="button"
          className="btn btn-ghost btn-sm perguntas-estruturar"
          disabled={estruturar.isPending}
          onClick={() => estruturar.mutate()}
        >
          <Wand2 size={13} />
          {estruturar.isPending ? 'Lendo as observações…' : 'Preencher períodos a partir das observações'}
        </button>
      )}

      <div className="perguntas-lista">
        {perguntas.map(p => {
          const resposta = respostas[p.id] || {};
          const opcoes = opcoesDaPergunta(p.origem);
          const exigeJustificativa = EXIGEM_JUSTIFICATIVA.includes(resposta.valor);
          return (
            <div key={p.id} className="pergunta" data-testid={`pergunta-${p.id}`}>
              <div className="pergunta-enunciado">{p.pergunta}</div>
              <div className="pergunta-contexto">{p.contexto}</div>
              {p.aguarda_nova_versao && (
                <div className="pergunta-dispensa-anterior">
                  Resposta registrada, mas a peça atual ainda não a cumpre: corrija ou gere a peça de
                  novo, ou marque “Já atendida” se a peça já trata disso.
                </div>
              )}
              {p.dispensa_anterior && (
                <div className="pergunta-dispensa-anterior">
                  Dispensada antes (“{p.dispensa_anterior.justificativa}”), mas o que a ficha diz
                  mudou desde então.
                </div>
              )}

              {p.tipo === 'confirmacao' ? (
                <div className="pergunta-opcoes">
                  {opcoes.map(([rotulo, valor]) => (
                    <button
                      key={rotulo} type="button"
                      onClick={() => definir(p.id, 'valor', valor)}
                      className={`btn btn-sm pergunta-opcao${resposta.valor === valor ? ' is-selecionada' : ''}`}
                      aria-pressed={resposta.valor === valor}
                    >
                      {rotulo}
                    </button>
                  ))}
                  <input
                    type="text"
                    className="pergunta-campo pergunta-campo-flex"
                    aria-label={exigeJustificativa ? 'Justificativa' : 'Como deve constar da peça'}
                    placeholder={exigeJustificativa
                      ? 'Justificativa (obrigatória)'
                      : 'Como deve constar da peça (opcional)'}
                    value={resposta.observacao || ''}
                    onChange={e => definir(p.id, 'observacao', e.target.value)}
                  />
                </div>
              ) : Array.isArray(resposta.valor) ? (
                // Períodos propostos a partir das observações: o atendente confere e salva.
                <div className="pergunta-periodos">
                  {resposta.valor.map((periodo, i) => (
                    <div key={i} className="pergunta-periodo">{resumoDoPeriodo(periodo)}</div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm pergunta-descartar"
                    onClick={() => definir(p.id, 'valor', '')}
                  >
                    Descartar proposta
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  className="pergunta-campo"
                  placeholder={
                    p.tipo === 'valor' ? 'Ex.: 600,00'
                      : p.tipo === 'data' ? 'Ex.: 06/06/2025'
                        : p.tipo === 'numero' ? 'Ex.: 1'
                          : 'Como consta do atendimento'
                  }
                  value={resposta.valor || ''}
                  onChange={e => definir(p.id, 'valor', e.target.value)}
                />
              )}

              {p.dispensavel && (dispensando === p.id ? (
                <div className="pergunta-dispensa">
                  <input
                    type="text"
                    className="pergunta-campo pergunta-campo-flex"
                    aria-label="Por que não se aplica"
                    placeholder="Por que não se aplica ao caso (obrigatório)"
                    value={justificativaDispensa}
                    onChange={e => setJustificativaDispensa(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={!justificativaDispensa.trim() || dispensar.isPending}
                    onClick={() => dispensar.mutate({ id: p.id, justificativa: justificativaDispensa.trim() })}
                  >
                    {dispensar.isPending ? 'Dispensando…' : 'Dispensar'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setDispensando(null); setJustificativaDispensa(''); }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm pergunta-nao-se-aplica"
                  onClick={() => { setDispensando(p.id); setJustificativaDispensa(''); }}
                >
                  Não se aplica
                </button>
              ))}

              <div className="pergunta-rodape">Afeta: {p.afeta} · aba {p.aba}</div>
            </div>
          );
        })}
      </div>

      {perguntas.length > 0 && (
        <button
          type="button"
          className="btn btn-primary btn-sm perguntas-salvar"
          disabled={!preenchidas.length || responder.isPending}
          onClick={() => responder.mutate(Object.fromEntries(preenchidas))}
        >
          <CheckCircle2 size={13} />
          {responder.isPending ? 'Salvando…' : `Salvar ${preenchidas.length || ''} resposta(s) na ficha`}
        </button>
      )}

      {dispensadas.length > 0 && (
        <details className="perguntas-dispensadas">
          <summary>{dispensadas.length} pendência(s) dispensada(s) ou encerrada(s)</summary>
          {dispensadas.map(d => (
            <div key={d.id} className="pergunta-dispensada">
              <div>
                <strong>{d.pedido}</strong>
                <span className="pergunta-dispensada-estado">{ROTULO_DO_ESTADO[d.estado] || 'Dispensada'}</span>
                {d.justificativa && (
                  <span className="pergunta-dispensada-justificativa">“{d.justificativa}”</span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={reabrir.isPending}
                onClick={() => reabrir.mutate(d.id)}
              >
                <RotateCcw size={12} /> Reabrir
              </button>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
